import Masonry from 'masonry-layout';

/**
 * Stable Projects Archive masonry adapter.
 *
 * The archive always has a responsive CSS Grid fallback. Masonry only takes
 * ownership after desktop geometry is measurable. This prevents the visible
 * "everything in column one" failure state when JS starts before layout has
 * settled or after a resize/filter cycle.
 */
export function createProjectMasonry(grid, {
    itemSelector = '[data-project-card]',
    breakpoint = 1024,
    gutter = 36,
    columns = 3,
    reducedMotion = false,
} = {}) {
    if (!grid) return null;

    const media = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const cards = () => [...grid.querySelectorAll(itemSelector)];
    let instance = null;
    let destroyed = false;
    let frame = 0;
    let retryTimer = 0;
    let bootToken = 0;
    let bootFrameA = 0;
    let bootFrameB = 0;
    let lastGridWidth = 0;
    let observedWidth = 0;
    let queuedLayout = null;
    let frozen = false;

    const visibleCards = () => cards().filter((card) => !card.hidden);

    const clearOwnedStyles = () => {
        grid.removeAttribute('data-package-masonry-ready');
        grid.removeAttribute('data-package-masonry-booting');
        grid.style.removeProperty('height');
        cards().forEach((card) => {
            card.style.removeProperty('position');
            card.style.removeProperty('left');
            card.style.removeProperty('top');
            card.style.removeProperty('transform');
        });
    };

    const destroyInstance = ({ fallback = true } = {}) => {
        instance?.destroy();
        instance = null;
        if (fallback) clearOwnedStyles();
    };

    const geometry = () => {
        const gridWidth = grid.getBoundingClientRect().width;
        const expectedCardWidth = (gridWidth - (gutter * (columns - 1))) / columns;
        const card = visibleCards()[0] || null;
        const cardWidth = card ? card.getBoundingClientRect().width : expectedCardWidth;
        const valid = media.matches
            && gridWidth >= 960
            && cardWidth > 240
            && Math.abs(cardWidth - expectedCardWidth) <= 6;

        return { gridWidth, cardWidth, expectedCardWidth, valid };
    };

    const syncIgnoredItems = () => {
        if (!instance) return;
        cards().forEach((card) => {
            if (card.hidden) instance.ignore(card);
            else instance.unignore(card);
        });
    };

    const finishBoot = (token, onReady) => {
        if (destroyed || frozen || token !== bootToken) return;
        grid.removeAttribute('data-package-masonry-booting');
        grid.dataset.packageMasonryReady = 'true';
        lastGridWidth = grid.getBoundingClientRect().width;
        onReady?.();

        if (queuedLayout) {
            const queued = queuedLayout;
            queuedLayout = null;
            window.requestAnimationFrame(() => layout(queued));
        }
    };

    const boot = ({ onReady = null } = {}) => {
        if (destroyed || frozen) return;
        if (!media.matches) {
            destroyInstance({ fallback: true });
            onReady?.();
            return;
        }

        const token = ++bootToken;
        window.clearTimeout(retryTimer);

        // Measure while the safe CSS Grid fallback is still active.
        const initial = geometry();
        if (!initial.valid) {
            destroyInstance({ fallback: true });
            retryTimer = window.setTimeout(() => boot({ onReady }), 80);
            return;
        }

        grid.dataset.packageMasonryBooting = 'true';
        grid.dataset.packageMasonryReady = 'true';

        window.requestAnimationFrame(() => {
            if (destroyed || frozen || token !== bootToken) return;
            const measured = geometry();
            if (!measured.valid) {
                clearOwnedStyles();
                retryTimer = window.setTimeout(() => boot({ onReady }), 80);
                return;
            }

            destroyInstance({ fallback: false });
            instance = new Masonry(grid, {
                itemSelector,
                columnWidth: measured.cardWidth,
                gutter,
                percentPosition: false,
                horizontalOrder: false,
                originLeft: document.documentElement.dir !== 'rtl',
                transitionDuration: 0,
                resize: false,
                initLayout: false,
            });
            syncIgnoredItems();

            let settled = false;
            const done = () => {
                if (settled) return;
                settled = true;
                if (instance) instance.options.transitionDuration = reducedMotion ? 0 : '0.46s';
                finishBoot(token, onReady);
            };

            instance.once('layoutComplete', done);
            instance.layout();
            window.setTimeout(done, 120);
        });
    };


    const scheduleBoot = () => {
        window.cancelAnimationFrame(bootFrameA);
        window.cancelAnimationFrame(bootFrameB);
        bootFrameA = window.requestAnimationFrame(() => {
            bootFrameB = window.requestAnimationFrame(() => boot());
        });
    };

    const layout = ({ immediate = false, onComplete = null } = {}) => {
        if (destroyed || frozen) {
            onComplete?.();
            return;
        }

        if (!media.matches) {
            destroyInstance({ fallback: true });
            onComplete?.();
            return;
        }

        if (!instance) {
            queuedLayout = { immediate, onComplete };
            scheduleBoot();
            return;
        }

        const measured = geometry();
        const widthChanged = Math.abs(measured.gridWidth - lastGridWidth) > 2;
        const invalidGeometry = !measured.valid;

        if (widthChanged || invalidGeometry) {
            queuedLayout = { immediate, onComplete };
            destroyInstance({ fallback: true });
            boot();
            return;
        }

        syncIgnoredItems();
        const previousDuration = instance.options.transitionDuration;
        instance.options.transitionDuration = immediate || reducedMotion ? 0 : '0.46s';

        let completeCalled = false;
        const complete = () => {
            if (completeCalled) return;
            completeCalled = true;
            if (instance) instance.options.transitionDuration = previousDuration;
            onComplete?.();
        };

        instance.once('layoutComplete', complete);
        instance.layout();
        window.setTimeout(complete, immediate || reducedMotion ? 50 : 560);
    };

    const request = () => {
        if (destroyed || frozen) return;
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
            frame = 0;
            if (!media.matches) {
                destroyInstance({ fallback: true });
                return;
            }

            const width = grid.getBoundingClientRect().width;
            if (!instance || Math.abs(width - lastGridWidth) > 2) {
                destroyInstance({ fallback: true });
                scheduleBoot();
                return;
            }

            layout({ immediate: true });
        });
    };

    const resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width || grid.getBoundingClientRect().width;
        if (Math.abs(width - observedWidth) < 0.5) return;
        observedWidth = width;
        request();
    });
    resizeObserver.observe(grid);
    media.addEventListener?.('change', request);
    window.addEventListener('orientationchange', request, { passive: true });

    // Card media uses authored aspect-ratio boxes, so image decode does not change
    // card geometry. Avoid image-load relayout storms while the user scrolls.
    document.fonts?.ready?.then(request).catch?.(() => {});

    // Give Tailwind/media-query styles two paint frames before Masonry takes over.
    scheduleBoot();

    return {
        layout,
        refresh({ immediate = true } = {}) {
            layout({ immediate });
        },
        freeze() {
            if (destroyed || frozen) return;
            frozen = true;
            window.cancelAnimationFrame(frame);
            window.cancelAnimationFrame(bootFrameA);
            window.cancelAnimationFrame(bootFrameB);
            window.clearTimeout(retryTimer);
            queuedLayout = null;
            resizeObserver.disconnect();
            media.removeEventListener?.('change', request);
            window.removeEventListener('orientationchange', request);
            // Deliberately keep Masonry's current inline geometry intact.
            // Clearing it during navigation causes a visible CSS-grid reflow
            // immediately before the browser paints the destination page.
        },
        resume() {
            if (destroyed || !frozen) return;
            frozen = false;
            observedWidth = grid.getBoundingClientRect().width;
            resizeObserver.observe(grid);
            media.addEventListener?.('change', request);
            window.addEventListener('orientationchange', request, { passive: true });

            if (!media.matches) {
                destroyInstance({ fallback: true });
                return;
            }

            if (instance) {
                lastGridWidth = grid.getBoundingClientRect().width;
                layout({ immediate: true });
            } else {
                scheduleBoot();
            }
        },
        destroy() {
            destroyed = true;
            window.cancelAnimationFrame(frame);
            window.cancelAnimationFrame(bootFrameA);
            window.cancelAnimationFrame(bootFrameB);
            window.clearTimeout(retryTimer);
            resizeObserver.disconnect();
            media.removeEventListener?.('change', request);
            window.removeEventListener('orientationchange', request);
            destroyInstance({ fallback: true });
        },
    };
}
